import { Component, OnInit, OnDestroy } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

import { NgbActiveModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { EventManager, AlertService, DateUtils } from 'ng-jhipster';

import { Card } from './card.model';
import { CardPopupService } from './card-popup.service';
import { CardService } from './card.service';
import { School, SchoolService } from '../school';
import { Student, StudentTypeaheadService } from '../student';
import { CardType, CardTypeService } from '../card-type';

import { Principal , CRUD_ACTIONS, CARD_UNLIMITED_BOOKINGS_NUMBER } from '../../shared/index';
import { AbstractDialogComponent } from '../abstract-dialog-component/abstract-dialog.component';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap/datepicker/ngb-date-struct';
import { SchoolCalendar, SchoolCalendarService} from '../school-calendar';
import { UnavailableDate } from '../unavailable-date';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';

@Component({
    selector: 'jhi-card-dialog',
    templateUrl: './card-dialog.component.html'
})
export class CardDialogComponent extends AbstractDialogComponent implements OnInit {

    card: Card;
    authorities: any[];
    isSaving: boolean;
    students: Student[];
    cardtypes: CardType[];
    search = this.studentTypeaheadService.search;
    resultFormatter = this.studentTypeaheadService.resultFormatter;
    currentSchoolCalendar: SchoolCalendar = new SchoolCalendar();
    schoolCalendars: SchoolCalendar[] = [];
    isClassTypeAPackage: boolean;
    isClassTypeOneTimeOnly: boolean;
    isExistingCard: boolean;

    inputFormatter = (student: any) => this.studentTypeaheadService.inputFormatter(student, () => this.card.student['user']);

    constructor(
        public activeModal: NgbActiveModal,
        alertService: AlertService,
        private cardService: CardService,
        schoolService: SchoolService,
        private cardTypeService: CardTypeService,
        private eventManager: EventManager,
        private studentTypeaheadService: StudentTypeaheadService,
        private schoolCalendarService: SchoolCalendarService,
        private dateUtils: DateUtils,
        principal: Principal
    ) {
        super(schoolService, alertService, principal);
    }

    ngOnInit() {
        this.isSaving = false;
        this.isExistingCard = this.card.id !== undefined;
        this.authorities = ['ROLE_USER', 'ROLE_ADMIN'];
        this.loadSchools();
        this.loadCurrentCalendar();
        this.cardTypeService.search({query: 'active:true'})
                            .subscribe(
                                (res: HttpResponse<Card[]>) => { this.cardtypes = res.body; },
                                (res: HttpErrorResponse) => this.onError(res));
        if (this.card.validUntil instanceof Date) {
            this.card.validUntil = {
                year: this.card.validUntil.getFullYear(),
                month: this.card.validUntil.getMonth() + 1,
                day: this.card.validUntil.getDate()
            };
        }

        if (this.card.validSince instanceof Date) {
            this.card.validSince = {
                year: this.card.validSince.getFullYear(),
                month: this.card.validSince.getMonth() + 1,
                day: this.card.validSince.getDate()
            };
        }
    }

    private loadCurrentCalendar() {
        this.principal.hasAnyAuthority(['ROLE_SCHOOL_ADMIN', 'ROLE_TEACHER', 'ROLE_ADVANCED_TEACHER' ])
        .then(response => {
            if (response) {
                this.schoolCalendarService
                .getAllFromCurrentDate()
                .subscribe(
                    (res: HttpResponse<SchoolCalendar[]>) => {
                        let calendars = res.body;
                        if (calendars.length > 0 ) {
                            this.schoolCalendars = calendars;
                            this.currentSchoolCalendar = this.getCurrentCalendar(calendars);
                            // We set the since only if creating a new Card
                            if (!this.card.id) {
                                this.setValidSinceDate();
                            }
                        } else {
                            this.currentSchoolCalendar = null;
                        }
                    },
                    (res: HttpErrorResponse) => this.onError(res));
            }
        });
    }

    /**
     * Returns the current calendar from the received SchoolCalendar array;
     * undefined if there is no calendar for the current date.
     */
    private getCurrentCalendar(calendars: SchoolCalendar[], date?: Date): SchoolCalendar|undefined {

        if (!calendars) {
            return ;
        }
        let current = !date ? new Date() : date;
        current.setHours(0, 0, 0, 0);

        return calendars.find( calendar => {
            return (calendar.begining.getTime() <= current.getTime()
                        && current.getTime() <= calendar.ending.getTime());
                    });

    }

    /**
     * Returns the next calendar starting after the specified date.
     * @param calendars
     * @param date
     */
    getNextCalendar(calendars: SchoolCalendar[], date: Date) {
        let nextCalendar: SchoolCalendar;
        calendars.forEach(calendar => {
            if (date.getTime() < calendar.begining.getTime()) {
                if (!nextCalendar || calendar.begining.getTime() < nextCalendar.begining.getTime()) {
                    nextCalendar = calendar;
                }
            }
        });
        return nextCalendar;
    }

    clear() {
        this.activeModal.dismiss('cancel');
    }

    onCardTypeChange(cardType: CardType) {
       this.card.price = cardType.price;
       this.card.maxAssistancesNum = cardType.maxAssistancesNum;
       this.isClassTypeAPackage = cardType.package_;
       this.isClassTypeOneTimeOnly = cardType.oneTimeOnly;
       this.setValidUntilDate();
    }

    onValidSinceChange(validSince: NgbDateStruct) {
        this.setValidUntilDate();
    }

    onUnlimitedClassesCheckboxChange(event: Event): void {
        if (this.card.unlimitedClasses) {
            this.card.maxAssistancesNum = CARD_UNLIMITED_BOOKINGS_NUMBER;
        }
    }

    /**
     * Set the validSince date value in the form after the calculation
     */
    setValidSinceDate() {
        let validSince  = this.calculateValidSinceDate();

        this.card.validSince =  {
             year: validSince.getFullYear(),
             month: validSince.getMonth() + 1,
             day: validSince.getDate()
        };
    }

    /**
     * Sets the validUntil date depending on the card type and the valid since date.
     */
    private setValidUntilDate() {
        if (!this.card.cardType) {
            return ;
        }

        if ( this.card.validSince ) {
            let validSince: Date = new Date(this.card.validSince.year, this.card.validSince.month - 1, this.card.validSince.day);
            let validUntil  = this.calculateValidUntilDate(validSince, this.card.cardType.duration);

            if (validUntil) {
                this.card.validUntil =  {
                    year: validUntil.getFullYear(),
                    month: validUntil.getMonth() + 1,
                    day: validUntil.getDate()
                };
            }
        }
    }

    /**
     * Returns the default value for the validSince date
     */
    private calculateValidSinceDate() {
        let validSince = new Date();

        if (!this.currentSchoolCalendar) {

            let nextCalendar = this.getNextCalendar(this.schoolCalendars, validSince);
            validSince = this.getNextAvailableDate(nextCalendar.begining, nextCalendar);
        }

        return validSince;
    }

    /**
     * Returns the validUntil date for a card given the start date and the duration in
     * weeks (from the selected card type).
     */
    private calculateValidUntilDate(validSince: Date, durationInWeeks: number) {

        if (!validSince) {
            return;
        }

        if (this.isClassTypeAPackage) {
            // when is a package, we do not care if the school has a calendar
            let validUntil: Date =  new Date(validSince.getTime());
            validUntil.setFullYear(validSince.getFullYear() + 1);
            return validUntil;
        }

        if (!durationInWeeks) {
            return;
        }

        let durationInDays = durationInWeeks * 7;
        let currentCalendar = (!this.currentSchoolCalendar) ? this.getCurrentCalendar(this.schoolCalendars, validSince)
                                                            : this.currentSchoolCalendar;
        let validUntil: Date =  new Date(validSince.getTime());

        if (currentCalendar) {
            // Set the default validUntil date based on the duration of the card type
            validUntil.setDate(validUntil.getDate() + durationInDays);
            if (this.isDateAvailable(validSince, false, currentCalendar)) {
                let unavailableDates: UnavailableDate[] = this.getUnavailableDates(false, currentCalendar);
                // Count unavailable days which can't count for the validity period
                let unavailableDays: number = this.countUnavailableDays(validSince, validUntil, unavailableDates);
                // Add the unavailable days to the duration
                validUntil.setDate(validUntil.getDate() + unavailableDays);
                // If the calculated date is a holiday the next available date is set
                if (!this.isDateAvailable(validUntil, false, currentCalendar)) {
                    validUntil = this.getNextAvailableDate(validUntil, currentCalendar);
                }
            } else {
                // If the validSince date is a holiday the same date is set to validUntil
                validUntil = new Date(validSince.getTime());
            }
            // if validUntil is greater than the end of the calendar, we force it to be the end of the calendar
            if (validUntil > currentCalendar.ending) {
                validUntil = currentCalendar.ending;
            }
        }
        return validUntil;
    }

    /**
     * Returns the number of unavailable days included in the UnavailableDate objects,
     * starting at the specified date.
     */
    countUnavailableDays(startDate: Date, endDate: Date, unavailableDates: UnavailableDate[]) {

        let days: number = 0;
        unavailableDates.forEach(dateInterval => {
            let begining: Date = this.dateUtils.convertLocalDateFromServer(dateInterval.begining);
            let ending: Date = this.dateUtils.convertLocalDateFromServer(dateInterval.ending);

            // Exclude unavailable dates outside the specified date range
            if (ending.getTime() < startDate.getTime() || begining.getTime() > endDate.getTime()) {
                return;
            }
            // If the startDate is inside this unavailable period we count only the days after that day
            if (startDate.getTime() > begining.getTime() ) {
                begining = new Date(startDate.getTime());
            }

            days += Math.round(Math.abs((ending.getTime() - begining.getTime()) / (1000 * 3600 * 24))) + 1;
        });

        return days;

    }

    /**
     * Returns the first available date after the specified date.
     */
    getNextAvailableDate(date: Date, calendar?: SchoolCalendar) {
        let unavailableDate: UnavailableDate;
        while (unavailableDate = this.getNextUnavailableDate(date, false, calendar)) {

            // While there is an unavailable date period containing the validUntil date
            // we take the end date of that period and move to the next day
            let ending: Date = this.dateUtils.convertLocalDateFromServer(unavailableDate.ending);
            if (date.getTime() < ending.getTime()) {
                date.setTime(ending.getTime());
            }
            date.setDate(date.getDate() + 1);

        }

        return date;
    }

    /**
     * Returns an array containing the unavailable date ranges.
     * @param useAllCalendars true to include all calendars. false to use only the current one.
     */
    getUnavailableDates(useAllCalendars?: Boolean, calendar?: SchoolCalendar) {
        let unavailableDates: UnavailableDate[];

        if (!useAllCalendars) {
            if (!calendar) {
                calendar = this.currentSchoolCalendar;
            }
            unavailableDates = calendar.unavailableDates;
        } else if (this.schoolCalendars.length > 0) {
            unavailableDates = [];
            this.schoolCalendars.forEach((cal) =>
                unavailableDates = unavailableDates.concat(cal.unavailableDates)
            );
        }
        return unavailableDates;
    }

    /**
     * Returns the next unavailable date range (UnavailableDate) containing the specified date if there is any.
     * It returns undefined otherwise.
     */
    getNextUnavailableDate(date: Date, useAllCalendars?: Boolean, calendar?: SchoolCalendar ): UnavailableDate {
        let unavailableDates: UnavailableDate[] = this.getUnavailableDates(useAllCalendars, calendar);

        if (!unavailableDates) {
            return undefined;
        }

        return unavailableDates.find(unavailableDate => {
            let begining: Date = this.dateUtils.convertLocalDateFromServer(unavailableDate.begining);
            let ending: Date = this.dateUtils.convertLocalDateFromServer(unavailableDate.ending);
            return (date.getTime() >= begining.getTime() && date.getTime() <= ending.getTime());
        });
    }

    /**
     * Returns true if the specified date is available; false otherwise.
     */
    isDateAvailable(date: Date, useAllCalendars?: Boolean, calendar?: SchoolCalendar) {

        if (!date) {
            return true;
        }
        let unavailableDates = this.getNextUnavailableDate(date, useAllCalendars, calendar);

        return (unavailableDates === undefined);
    }

    /**
     * Converts the specfied date from NgbDateStruct to a Date object and check if it is available.
     */
    isInputDateAvailable(inputDate: NgbDateStruct) {
        if (!inputDate) {
            return true;
        }
        let dateObj: Date = new Date(inputDate.year, inputDate.month - 1, inputDate.day);
        return this.isDateAvailable(dateObj, true);
    }

    /**
     * Checks if the validSince date is available.
     */
    isSinceDateAvailable() {
        return this.isInputDateAvailable(this.card.validSince);
    }

    /**
     * Checks if the validUntil date is available.
     */
    isUntilDateAvailable() {
        return this.isInputDateAvailable(this.card.validUntil);
    }

    isInputDateOutsideCalendar(inputDate: NgbDateStruct) {
        if (!inputDate) {
            return true;
        }
        let dateObj: Date = new Date(inputDate.year, inputDate.month - 1, inputDate.day);
        return !this.getCurrentCalendar(this.schoolCalendars, dateObj);
    }

    isSinceDateOutsideCalendar() {
        return this.isInputDateOutsideCalendar(this.card.validSince);
    }

    isUntilDateOutsideCalendar() {
        // we do not care if the until date is outside the calendar for anual packages
        return !this.isClassTypeAPackage && this.isInputDateOutsideCalendar(this.card.validUntil);
    }

    save(form: NgForm) {
        if (this.card.student instanceof Object) {
            this.isSaving = true;
            if (this.card.id !== undefined) {
                this.cardService.update(this.card)
                .subscribe((res: Card) =>
                    this.onSaveSuccess(res, CRUD_ACTIONS.UPDATE), (res: HttpErrorResponse) => this.onSaveError(res));
            } else {
                this.cardService.create(this.card)
                    .subscribe((res: Card) =>
                        this.onSaveSuccess(res, CRUD_ACTIONS.CREATE), (res: HttpErrorResponse) => this.onSaveError(res));
            }
        } else {
            let studentInput = form !== undefined ? form.controls['student'] : null;
            if (studentInput !== null) {
                studentInput.reset();
            }
        }
    }

    private onSaveSuccess(result: Card, action: String) {
        this.eventManager.broadcast({ name: 'cardListModification', content: {
            action: action,
            content: result
        }});
        this.isSaving = false;
        this.activeModal.dismiss(result);
    }

    private onSaveError(error) {
        this.isSaving = false;
        this.onError(error);
    }

    public showCurrentCalendarWarning() {
        return this.currentSchoolCalendar === null;
    }

    trackSchoolById(index: number, item: School) {
        return item.id;
    }

    trackStudentById(index: number, item: Student) {
        return item.id;
    }

    trackCardTypeById(index: number, item: CardType) {
        return item.id;
    }
}

@Component({
    selector: 'jhi-card-popup',
    template: ''
})
export class CardPopupComponent implements OnInit, OnDestroy {

    modalRef: NgbModalRef;
    routeSub: any;

    constructor(
        private route: ActivatedRoute,
        private cardPopupService: CardPopupService
    ) { }

    ngOnInit() {
        this.routeSub = this.route.params.subscribe(params => {
            if (params['id']) {
                this.modalRef = this.cardPopupService
                    .open(CardDialogComponent as Component, params['id']);
            } else if (params['studentId']) {
                this.modalRef = this.cardPopupService
                    .open(CardDialogComponent as Component, undefined, params['studentId']);
            } else {
                this.modalRef = this.cardPopupService
                    .open(CardDialogComponent as Component);
            }

        });

    }

    ngOnDestroy() {
        this.routeSub.unsubscribe();
    }
}



// WEBPACK FOOTER //
// ./src/main/webapp/app/entities/card/card-dialog.component.ts